import { Button, Input, toast } from "@gotts.ai/ui";
import { AlertTriangle, Minus, Pickaxe, Plus } from "lucide-react";
import { useCallback, useState } from "react";

import { useBlockNumber } from "../../hooks/useBlockNumber";
import { useRpcRequest } from "../../hooks/useRpcRequest";
import { useSimulatedTime } from "../../hooks/useSimulatedTime";

import { ChainStateHeader } from "./ChainStateHeader";
import { ConfirmModal } from "./ConfirmModal";
import { ImpersonateCard } from "./ImpersonateCard";
import { MiningControls } from "./MiningControls";
import { MintTokensCard } from "./MintTokensCard";
import { SetBalanceCard } from "./SetBalanceCard";
import { SnapshotControls } from "./SnapshotControls";
import { TimeControls } from "./TimeControls";
import { WrapUnwrapCard } from "./WrapUnwrapCard";

import type { MiningMode } from "./MiningControls";

const MINE_PRESETS = [1, 5, 10, 50, 100];

export default function ChainControls() {
  const { data: blockNumber } = useBlockNumber();
  const { request } = useRpcRequest();
  const { timestamp: simulatedTimestamp } = useSimulatedTime();

  const [loading, setLoading] = useState<string | null>(null);
  const [snapshotId, setSnapshotId] = useState<string | null>(null);
  const [blockPulse, setBlockPulse] = useState(false);
  const [miningMode, setMiningMode] = useState<MiningMode>("auto");
  const [activeImpersonation, setActiveImpersonation] = useState<string | null>(
    null,
  );
  const [mineNCount, setMineNCount] = useState(10);

  // ConfirmModal state for revert
  const [revertConfirmOpen, setRevertConfirmOpen] = useState(false);

  async function withLoading(key: string, fn: () => Promise<void>) {
    setLoading(key);
    try {
      await fn();
    } catch (err) {
      toast.error(`Error: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading(null);
    }
  }

  const triggerBlockPulse = useCallback(() => {
    setBlockPulse(true);
    setTimeout(() => {
      setBlockPulse(false);
    }, 600);
  }, []);

  async function handleMineN() {
    const count = mineNCount;
    await withLoading("mineN", async () => {
      for (let i = 0; i < count; i++) {
        await request("evm_mine");
      }
      triggerBlockPulse();
      toast.success(`Mined ${String(count)} block${count === 1 ? "" : "s"}`);
    });
  }

  async function handleSnapshot() {
    await withLoading("snapshot", async () => {
      const id = await request("evm_snapshot");
      setSnapshotId(id as string);
      toast.success(`Snapshot created: ${String(id)}`);
    });
  }

  function handleRevertClick() {
    if (!snapshotId) return;
    setRevertConfirmOpen(true);
  }

  async function handleRevertConfirm() {
    if (!snapshotId) return;
    setRevertConfirmOpen(false);
    await withLoading("revert", async () => {
      await request("evm_revert", [snapshotId]);
      toast.success(`Reverted to snapshot ${snapshotId}`);
      setSnapshotId(null);
    });
  }

  async function handleStopBannerImpersonating() {
    if (!activeImpersonation) return;
    await withLoading("stopImpersonate", async () => {
      await request("anvil_stopImpersonatingAccount", [activeImpersonation]);
      setActiveImpersonation(null);
    });
  }

  const sharedProps = {
    loading,
    withLoading,
    simulatedTimestamp,
    blockNumber,
  };

  return (
    <div className="space-y-6">
      {/* Impersonation warning banner */}
      {activeImpersonation && (
        <div className="impersonation-banner flex items-center gap-2">
          <AlertTriangle size={14} />
          <span>
            Impersonating{" "}
            <span className="font-mono">
              {activeImpersonation.slice(0, 6)}...
              {activeImpersonation.slice(-4)}
            </span>
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={handleStopBannerImpersonating}
            loading={loading === "stopImpersonate"}
            disabled={loading !== null}
            className="btn-danger ml-auto"
          >
            Stop Impersonating
          </Button>
        </div>
      )}

      {/* Chain State Header */}
      <ChainStateHeader
        simulatedTimestamp={simulatedTimestamp}
        blockNumber={blockNumber}
        blockPulse={blockPulse}
        miningMode={miningMode}
      />

      {/* Two-column grid: open and breathing */}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        {/* Left column — Chain Operations */}
        <div className="space-y-5">
          {/* Mining Mode + Mine Block — combined card */}
          <div className="glass-card space-y-5 p-5">
            <div className="flex items-center justify-between">
              <h3 className="font-sans text-sm font-medium text-[var(--color-text)]">
                Block Production
              </h3>
              <span className="font-mono text-[10px] tracking-wider text-[var(--color-text-muted)]">
                {miningMode.toUpperCase()} MODE
              </span>
            </div>

            <MiningControls
              {...sharedProps}
              miningMode={miningMode}
              onMiningModeChange={setMiningMode}
            />

            {/* Mine blocks */}
            <div className="border-t border-[var(--glass-border)] pt-4">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5 rounded-full border border-[var(--glass-border)] bg-[rgba(255,255,255,0.02)] px-1">
                  <button
                    type="button"
                    data-testid="mine-decrement"
                    className="flex h-8 w-8 items-center justify-center rounded-full text-[var(--color-text-muted)] transition-colors hover:bg-[rgba(255,255,255,0.06)] hover:text-[var(--color-text)]"
                    onClick={() => {
                      setMineNCount((c) => Math.max(1, c - 1));
                    }}
                    disabled={loading !== null}
                  >
                    <Minus size={14} />
                  </button>
                  <Input
                    type="number"
                    value={String(mineNCount)}
                    onChange={(e) => {
                      setMineNCount(
                        Math.max(1, parseInt(e.target.value, 10) || 1),
                      );
                    }}
                    className="w-12 border-0 bg-transparent text-center !text-base"
                  />
                  <button
                    type="button"
                    data-testid="mine-increment"
                    className="flex h-8 w-8 items-center justify-center rounded-full text-[var(--color-text-muted)] transition-colors hover:bg-[rgba(255,255,255,0.06)] hover:text-[var(--color-text)]"
                    onClick={() => {
                      setMineNCount((c) => c + 1);
                    }}
                    disabled={loading !== null}
                  >
                    <Plus size={14} />
                  </button>
                </div>

                <Button
                  variant="gold"
                  size="sm"
                  onClick={handleMineN}
                  loading={loading === "mineN"}
                  disabled={loading !== null}
                  className="flex-1"
                >
                  <Pickaxe size={14} className="mr-1.5" />
                  Mine {mineNCount} block{mineNCount === 1 ? "" : "s"}
                </Button>
              </div>

              {/* Preset chips */}
              <div className="mt-3 flex flex-wrap gap-1.5">
                {MINE_PRESETS.map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`btn-percentage-pill px-3 py-1 text-[10px] ${mineNCount === n ? "btn-percentage-pill-selected" : ""}`}
                    onClick={() => {
                      setMineNCount(n);
                    }}
                    disabled={loading !== null}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Snapshots */}
          <div className="glass-card p-5">
            <h3 className="mb-4 font-sans text-sm font-medium text-[var(--color-text)]">
              Snapshots
            </h3>
            <SnapshotControls
              {...sharedProps}
              snapshotId={snapshotId}
              onSnapshot={handleSnapshot}
              onRevert={handleRevertClick}
            />
          </div>

          {/* Time Controls */}
          <div className="glass-card p-5">
            <h3 className="mb-4 font-sans text-sm font-medium text-[var(--color-text)]">
              Time Travel
            </h3>
            <TimeControls {...sharedProps} />
          </div>
        </div>

        {/* Right column — Account & Token Operations */}
        <div className="space-y-5">
          <div className="glass-card p-5">
            <h3 className="mb-4 font-sans text-sm font-medium text-[var(--color-text)]">
              Set ETH Balance
            </h3>
            <SetBalanceCard {...sharedProps} />
          </div>

          <div className="glass-card p-5">
            <h3 className="mb-4 font-sans text-sm font-medium text-[var(--color-text)]">
              Mint Tokens
            </h3>
            <MintTokensCard {...sharedProps} />
          </div>

          <div className="glass-card p-5">
            <h3 className="mb-4 font-sans text-sm font-medium text-[var(--color-text)]">
              Wrap & Unwrap
            </h3>
            <WrapUnwrapCard {...sharedProps} />
          </div>

          <div className="glass-card p-5">
            <h3 className="mb-4 font-sans text-sm font-medium text-[var(--color-text)]">
              Impersonate Account
            </h3>
            <ImpersonateCard
              {...sharedProps}
              activeImpersonation={activeImpersonation}
              onImpersonationChange={setActiveImpersonation}
            />
          </div>
        </div>
      </div>

      {/* Revert confirm modal */}
      <ConfirmModal
        open={revertConfirmOpen}
        onClose={() => {
          setRevertConfirmOpen(false);
        }}
        onConfirm={handleRevertConfirm}
        title="Revert to Snapshot"
        description={`Revert to snapshot ${snapshotId ?? ""}? This will discard all state changes since the snapshot was taken.`}
        confirmLabel="Revert"
        confirmVariant="danger"
        loading={loading === "revert"}
      />
    </div>
  );
}
